/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'

interface Props {
  /** What was decided — "Role", "Team", "Division". */
  label: string
  /** The decision itself. */
  value: string
  /** Why it is already answered, e.g. "set by this link". */
  reason?: string
  /** Offered only when the reader may still change their mind. */
  onChange?: () => void
}

/**
 * A question that no longer needs asking, shown as one line.
 *
 * Collapsing answered questions is what lets three questions share a screen
 * without it becoming two screens stacked. They collapse rather than disappear:
 * somebody who followed a link that fixed their team needs to see that before
 * paying, not discover it afterwards.
 */
const ResolvedRow = ({ label, value, reason, onChange }: Props) => (
  <div css={rowStyle}>
    <Text variant='paragraphXSmall' weight='semiBold' color={colors.NEUTRAL_40}>
      {capitalizeWords(label)}
    </Text>
    <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
      {value}
    </Text>
    {reason || onChange ? (
      <div css={trailingGroupStyle}>
        {reason ? (
          <span css={reasonStyle}>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_40}
            >
              {reason}
            </Text>
          </span>
        ) : null}
        {onChange ? (
          <button type='button' css={changeButtonStyle} onClick={onChange}>
            <Text
              variant='paragraphXSmall'
              weight='semiBold'
              color={colors.PRIMARY}
            >
              {capitalizeWords('Change')}
            </Text>
          </button>
        ) : null}
      </div>
    ) : null}
  </div>
)

export default ResolvedRow

const rowStyle = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${colors.NEUTRAL_600};
  border-left: 3px solid ${colors.PRIMARY};
  border-radius: 8px;
  background-color: ${colors.NEUTRAL_700};
`

/* One trailing group, so the reason and Change share the far end of the row
   without either needing to know whether the other rendered. */
const trailingGroupStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`

const reasonStyle = css`
  font-style: italic;
`

const changeButtonStyle = css`
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
`
